import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Shield, Search, Loader2, UserPlus, UserMinus } from 'lucide-react';
import { toast } from 'sonner';

const AdminManagementPanel = () => {
  const [admins, setAdmins] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [searching, setSearching] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const res = await api.getAdminUsers();
      setAdmins(res.data.admins || []);
    } catch (err) {
      toast.error('Failed to load admins');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await api.searchUsers(searchQuery);
      setSearchResults(res.data.users || []);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleMakeAdmin = async (email) => {
    setActionLoading(email);
    try {
      await api.makeAdmin(email);
      toast.success(`${email} is now an admin`);
      loadAdmins();
      setSearchResults(prev => prev.map(u => u.email === email ? {...u, is_admin: true} : u));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant admin');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokeAdmin = async (email) => {
    setActionLoading(email);
    try {
      await api.revokeAdmin(email);
      toast.success(`Admin privileges revoked from ${email}`);
      loadAdmins();
      setSearchResults(prev => prev.map(u => u.email === email ? {...u, is_admin: false} : u));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke admin');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Current Admins */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
            <Shield className="h-5 w-5" />
            Current Admins
          </CardTitle>
          <CardDescription className="dark:text-slate-400">Users with admin privileges</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingAdmins ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : admins.length === 0 ? (
            <p className="text-sm text-slate-400">No admin users found</p>
          ) : (
            <div className="space-y-3">
              {admins.map((admin, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{admin.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{admin.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevokeAdmin(admin.email)}
                    disabled={actionLoading === admin.email}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                    data-testid={`revoke-admin-${admin.email}`}
                  >
                    {actionLoading === admin.email ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <UserMinus className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add New Admin */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
            <UserPlus className="h-5 w-5" />
            Add Admin
          </CardTitle>
          <CardDescription className="dark:text-slate-400">Search for a user to grant admin privileges</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search by email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                data-testid="admin-search-input"
              />
              <Button 
                onClick={handleSearch} 
                disabled={searching || !searchQuery.trim()}
                className="rounded-full"
                data-testid="admin-search-btn"
              >
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>

            {searchResults.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {searchResults.map((user, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 dark:text-slate-100">{user.email}</p>
                        {user.is_admin && (
                          <span className="text-xs bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full">Admin</span>
                        )}
                      </div>
                      {user.specialty && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">{user.specialty}</p>
                      )}
                    </div>
                    {user.is_admin ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevokeAdmin(user.email)}
                        disabled={actionLoading === user.email}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                        data-testid={`revoke-${user.email}`}
                      >
                        {actionLoading === user.email ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <UserMinus className="h-4 w-4 mr-1" />
                            Revoke
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMakeAdmin(user.email)}
                        disabled={actionLoading === user.email}
                        className="text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                        data-testid={`grant-${user.email}`}
                      >
                        {actionLoading === user.email ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="h-4 w-4 mr-1" />
                            Grant Admin
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {searchQuery && searchResults.length === 0 && !searching && (
              <p className="text-sm text-slate-400 text-center py-4">No users found matching "{searchQuery}"</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminManagementPanel;
